import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {number | null} */
  #keepOpenInterval = null;
  /** @type {number | null} */
  #keepOpenTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // Ensure cart drawer stays open during individual item removal
    const cartDrawer = document.querySelector('cart-drawer-component');
    const wasDrawerOpen = cartDrawer && cartDrawer.querySelector('[ref="dialog"]')?.hasAttribute('open');
    // keep the drawer open briefly to avoid close/reopen flicker
    this.#startKeepDrawerOpen(1800);
    try {
      // Broadcast a lock-open request so the drawer won’t close mid-update
      this.dispatchEvent(new CustomEvent('cart-drawer:lock-open', { bubbles: true, detail: { ms: 1800 } }));
      document.dispatchEvent(new CustomEvent('cart-drawer:lock-open', { detail: { ms: 1800 } }));
    } catch (_) {}
    
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => {
      cartItemRowToRemove.remove();
      // Reopen drawer if it was open before removal
      if (wasDrawerOpen && cartDrawer && 'open' in cartDrawer && typeof /** @type {any} */(cartDrawer).open === 'function') {
        setTimeout(() => {
          try {
            /** @type {any} */(cartDrawer).open();
          } catch (e) {
            console.warn('Failed to reopen cart drawer:', e);
          }
        }, 100);
      }
    };

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // Prevent automatic discount application when adding products to empty cart
        this.#preventAutomaticDiscounts();

        // If cart became empty, clear discount codes server-side as well
        if (newCartItemCount === 0) {
          try {
            const cfg = fetchConfig('json', { body: JSON.stringify({ discount: '' }) });
            fetch(Theme.routes.cart_update_url, cfg).catch(() => {});
          } catch (_) {}
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.#stopKeepDrawerOpen();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    // Ensure cart drawer stays open during cart updates from individual item removal
    const cartDrawer = document.querySelector('cart-drawer-component');
    const wasDrawerOpen = cartDrawer && cartDrawer.querySelector('[ref="dialog"]')?.hasAttribute('open');

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // Reopen drawer if it was open before the update
    if (wasDrawerOpen && cartDrawer && 'open' in cartDrawer && typeof /** @type {any} */(cartDrawer).open === 'function') {
      setTimeout(() => {
        try {
          /** @type {any} */(cartDrawer).open();
        } catch (e) {
          console.warn('Failed to reopen cart drawer after update:', e);
        }
      }, 50);
    }

    // If cart became empty via manual removals, clear discounts server-side
    try {
      // event.detail.data.itemCount is our canonical place; fall back to 0 if unknown
      const nextCountRaw = (event && event.detail && event.detail.data && typeof event.detail.data.itemCount === 'number')
        ? event.detail.data.itemCount
        : NaN;
      const nextCount = Number(nextCountRaw);
      if (!Number.isNaN(nextCount) && nextCount === 0) {
        const cfg = fetchConfig('json', { body: JSON.stringify({ discount: '' }) });
        fetch(Theme.routes.cart_update_url, cfg).catch(() => {});
      }
    } catch (_) {}
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Keep the cart drawer open for a short period to avoid flicker.
   * @param {number} durationMs
   */
  #startKeepDrawerOpen(durationMs) {
    try {
      const drawer = document.querySelector('cart-drawer-component');
      const ensureOpen = () => {
        try {
          if (drawer && 'open' in drawer && typeof drawer.open === 'function') drawer.open();
          const dialog = drawer?.querySelector?.('[ref="dialog"]');
          if (dialog && !dialog.hasAttribute('open')) dialog.setAttribute('open', '');
        } catch (_) {}
      };
      ensureOpen();
      if (this.#keepOpenInterval) window.clearInterval(this.#keepOpenInterval);
      if (this.#keepOpenTimeout) window.clearTimeout(this.#keepOpenTimeout);
      this.#keepOpenInterval = window.setInterval(ensureOpen, 400);
      this.#keepOpenTimeout = window.setTimeout(() => this.#stopKeepDrawerOpen(), Math.max(800, durationMs));
    } catch (_) {}
  }

  #stopKeepDrawerOpen() {
    if (this.#keepOpenInterval) {
      window.clearInterval(this.#keepOpenInterval);
      this.#keepOpenInterval = null;
    }
    if (this.#keepOpenTimeout) {
      window.clearTimeout(this.#keepOpenTimeout);
      this.#keepOpenTimeout = null;
    }
  }

  /**
   * Prevents automatic discount application when adding products to empty cart.
   */
  #preventAutomaticDiscounts() {
    // Check if cart was empty before this update
    const cartDrawer = document.querySelector('cart-drawer-component');
    const wasEmpty = cartDrawer && cartDrawer.classList.contains('cart-drawer--empty');
    
    if (wasEmpty) {
      // Remove any automatic discount codes that might have been applied
      const discountCodes = document.querySelectorAll('.cart-discount__pill');
      discountCodes.forEach(pill => {
        const removeBtn = pill.querySelector('.cart-discount__pill-remove');
        if (removeBtn && 'click' in removeBtn && typeof removeBtn.click === 'function') {
          // Simulate click to remove automatic discount
          removeBtn.click();
        }
      });
      
      // Clear any discount input fields
      const discountInputs = document.querySelectorAll('.cart-discount__input');
      discountInputs.forEach(input => {
        if (input instanceof HTMLInputElement && input.value) {
          input.value = '';
        }
      });
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
